import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { DEFAULT_PAGINATION } from '~/utils/constants';

// Redux Actions
import { fetchQrCodeScannedContacts } from '~/redux/actions/qr-codes-actions';

// MUI Components
import { Box, Typography } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';

// Components
import NoRecords from '~/components/General/NoRecords';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import GlobalStyles from '@mui/material/GlobalStyles';

// Icons
import { CustomFieldIcon } from '~/assets/images';
import TruncatedText from '~/components/General/TruncatedText';

const QrCodeHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  // Redux State
  const scannedContacts = useSelector((state) => state.qrCodesReducers.scannedContacts);
  const totalCount = useSelector((state) => state.qrCodesReducers.scannedContactsRowsCount);
  const loading = useSelector((state) => state.qrCodesReducers.scannedContactsLoader);
  const from = useSelector((state) => state.qrCodesReducers.scannedContactsFrom);
  const to = useSelector((state) => state.qrCodesReducers.scannedContactsTo);
  const lastPage = useSelector((state) => state.qrCodesReducers.scannedContactsLastPage);
  const currentPage = useSelector((state) => state.qrCodesReducers.scannedContactsCurrentPage);
  const perPage = useSelector((state) => state.qrCodesReducers.scannedContactsPerPage);

  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: 20 });
  const [loadedRequestKey, setLoadedRequestKey] = useState(null);

  const qrCodeId = params.id;
  const requestKey = `${qrCodeId}:${pagination.page}:${pagination.pageSize}`;

  useEffect(() => {
    let isActive = true;

    const loadHistory = async () => {
      if (!qrCodeId) return;

      await dispatch(
        fetchQrCodeScannedContacts(qrCodeId, {
          page: pagination.page,
          pageSize: pagination.pageSize,
        })
      );

      if (isActive) {
        setLoadedRequestKey(requestKey);
      }
    };

    if (qrCodeId) {
      loadHistory();
    }

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, qrCodeId]);

  const showLoader = loadedRequestKey !== requestKey || loading;
  const pageSizes = [20, 50, 100];

  const handleChangePage = (event, newPage) => {
    setPagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const handleBackToQrCodes = () => {
    navigate('/engagement/qr-codes');
  };

  const columns = [
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText
          text={params.row?.contact?.firstName + ' ' + params.row?.contact?.lastName || ''}
          maxLength={30}
        />
      ),
    },
    {
      field: 'companyName',
      headerName: 'Company Name',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText text={params.row?.contact?.companyName || ''} maxLength={30} />
      ),
    },
    {
      field: 'mailingAddress',
      headerName: 'Mailing Address',
      flex: 1.5,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText
          htmlElement={
            <>
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {params.row?.contact?.address1 + ' ' + params.row?.contact?.address2}
                <br />
                {params.row?.contact?.city +
                  ', ' +
                  params.row?.contact?.state +
                  ' ' +
                  params.row?.contact?.zip}
              </Typography>
            </>
          }
          text={
            `${params?.row?.address1 || ''} ${params?.row?.address2 || ''}` +
            `${params?.row?.city || ''} ` +
            `${params?.row?.state || ''} ` +
            `${params?.row?.zip || ''} `
          }
          maxLength={30}
        />
      ),
    },
    {
      field: 'utmCampaignName',
      headerName: 'UTM Campaign Name',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText
          text={params?.row?.campaignName || params?.row?.utmCampaignName || ''}
          maxLength={30}
        />
      ),
    },
    {
      field: 'totalScans',
      headerName: 'Total Scans',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        return <Typography variant="body2">{params.row.totalScans || 0}</Typography>;
      },
    },
  ];

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper olcSettingsListPage qrCodeHistoryPageWrapper">
        <Box className="olcScreenCard olcSettingsListCard qrCodeHistoryScreenCard">
          <Box className="qrHistoryHeader olcHeaderWrap">
            <OlcPageBackButton onClick={handleBackToQrCodes}>Back to QR Codes</OlcPageBackButton>
          </Box>

          {!showLoader && scannedContacts.length === 0 ? (
            <Box>
              <NoRecords
                icon={
                  <img
                    src={CustomFieldIcon}
                    style={{ height: '33px', width: '33px' }}
                    alt="no-records"
                  />
                }
                title="No QR Code History"
                message={
                  <>
                    <p style={{ margin: '0px' }}>No QR code scanning history available yet.</p>
                    <p style={{ margin: '0px' }}>
                      QR codes will appear here once they are scanned.
                    </p>
                  </>
                }
              />
            </Box>
          ) : (
            <Box
              className={`olcTableGridInternal ${showLoader ? 'table-loading' : ''} ${scannedContacts.length === 0 ? 'table-empty' : ''}`}
            >
              <Box sx={{ position: 'relative' }}>
                <DataGrid
                  rows={showLoader ? [] : scannedContacts}
                  columns={columns}
                  getRowId={(row) => row.id}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  pagination={false}
                  rowHeight={61}
                  loading={showLoader}
                  getRowClassName={() => ''}
                />
              </Box>

              {(showLoader || totalCount > 0) && (
                <OlcTablePagination
                  count={lastPage || 1}
                  page={currentPage || 1}
                  pageSize={perPage || pagination.pageSize}
                  pageSizes={pageSizes}
                  onPageChange={handleChangePage}
                  onPageSizeChange={(event) => {
                    setPagination({ page: 1, pageSize: event.target.value });
                  }}
                  from={from}
                  to={to}
                  total={totalCount}
                  loading={showLoader}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default QrCodeHistory;
